import { useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, ErrorText, Spinner } from '../components/ui';
import { acceptInvite, getInvite } from '../lib/api';
import { useLogout, useSession } from '../features/auth/useSession';
import { SESSION_KEY } from '../features/auth/useSession';

/** Landing page for an invite link: shows who invited you and lets you join. */
export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isLoading: sessionLoading } = useSession();
  const logout = useLogout();

  const invite = useQuery({ queryKey: ['invite', token], queryFn: () => getInvite(token) });

  const accept = useMutation({
    mutationFn: () => acceptInvite(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      navigate('/app', { replace: true });
    },
  });

  const info = invite.data;
  const emailMatches =
    !!user && !!info?.email && user.email.toLowerCase() === info.email.toLowerCase();

  // If you're already signed in as the invited person, join automatically —
  // no extra "Join" click. Guarded so it fires at most once.
  const autoTried = useRef(false);
  useEffect(() => {
    if (!autoTried.current && info?.status === 'pending' && emailMatches) {
      autoTried.current = true;
      accept.mutate();
    }
  }, [info?.status, emailMatches, accept]);

  return (
    <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center">
          <span className="text-lg font-semibold tracking-tight text-neutral-900">
            KatSquare Draft
          </span>
        </div>

        <Card className="p-6">
          {invite.isPending || sessionLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : !info || info.status === 'invalid' ? (
            <Message
              title="Invitation not found"
              body="This invite link is invalid or has been revoked."
              action={<LinkButton to="/app">Go to app</LinkButton>}
            />
          ) : info.status === 'accepted' && !emailMatches ? (
            <Message
              title="Invitation already used"
              body="This invitation has already been accepted."
              action={<LinkButton to="/login">Sign in</LinkButton>}
            />
          ) : (
            <>
              <h1 className="mb-1 text-lg font-semibold text-neutral-900">
                Join {info.workspaceName}
              </h1>
              <p className="mb-5 text-sm text-neutral-500">
                You've been invited to <strong>{info.workspaceName}</strong> as a{' '}
                {info.role === 'REVIEWER' ? 'Reviewer' : 'Manager'}.
              </p>

              {!user ? (
                // Not signed in → route to auth, carrying the invite so we return here.
                <div className="flex flex-col gap-2">
                  <LinkButton to={authLink('register', token, info.email)}>
                    Create an account to join
                  </LinkButton>
                  <p className="text-center text-sm text-neutral-500">
                    Already have an account?{' '}
                    <Link className="font-medium text-neutral-900 underline" to={authLink('login', token, info.email)}>
                      Sign in
                    </Link>
                  </p>
                </div>
              ) : emailMatches ? (
                <div className="flex flex-col gap-3">
                  <Button onClick={() => accept.mutate()} disabled={accept.isPending}>
                    {accept.isPending ? <Spinner /> : `Join ${info.workspaceName}`}
                  </Button>
                  {accept.isError && <ErrorText>{(accept.error as Error).message}</ErrorText>}
                </div>
              ) : (
                // Signed in as the wrong account.
                <div className="flex flex-col gap-3">
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    This invite is for <strong>{info.email}</strong>, but you're signed in as{' '}
                    <strong>{user.email}</strong>. Sign out and sign in with the invited email to accept.
                  </p>
                  <Button
                    variant="ghost"
                    className="border border-neutral-200"
                    onClick={() => {
                      void logout().then(() => queryClient.setQueryData(SESSION_KEY, null));
                    }}
                  >
                    Sign out
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

/** Build a /login or /register link that returns to this invite after auth. */
function authLink(mode: 'login' | 'register', token: string, email: string | null): string {
  const params = new URLSearchParams({ invite: token });
  if (email) params.set('email', email);
  return `/${mode}?${params.toString()}`;
}

function Message({ title, body, action }: { title: string; body: string; action: React.ReactNode }) {
  return (
    <div className="text-center">
      <h1 className="mb-1 text-lg font-semibold text-neutral-900">{title}</h1>
      <p className="mb-5 text-sm text-neutral-500">{body}</p>
      {action}
    </div>
  );
}

function LinkButton({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="block rounded-md bg-neutral-900 px-4 py-2 text-center text-sm font-medium text-white hover:bg-neutral-700"
    >
      {children}
    </Link>
  );
}
